/**
 * SaltieShoutOutReveal — the peaceful "open the letter" interaction.
 *
 * Trigger: clicking anywhere on the readable stationery block.
 *
 * Choreography (intentionally SLOW and quiet — no whoosh, no zoom
 * drama, no fanfare):
 *
 *   1. Backdrop breathes in over ~600ms. The rest of the room fades
 *      softly back but never fully disappears.
 *   2. Two distressed-white shutter panels appear CLOSED in front of
 *      the letter (like the ones on the wall above).
 *   3. Over ~2.6s (long, easeInOut), the shutters slide apart to
 *      reveal the letter.
 *   4. In parallel, the letter itself very gently eases forward
 *      (scale 1.00 → 1.05) as if the camera leaned in a hair.
 *   5. On close (click backdrop, ESC, or the tiny close pill), the
 *      whole sequence reverses at the same peaceful pace.
 *
 * Respects `prefers-reduced-motion`: skips the shutter slide and
 * cross-fades the letter in gently instead.
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import SaltieShoutOutStationery from "./SaltieShoutOutStationery";

/**
 * <ShutterPanel /> — one half of the reveal. Painted as distressed
 * warm white with six divided-light multi-pane windows to match the
 * shutter aesthetic in the hero. Purely CSS so it scales cleanly.
 */
function ShutterPanel({ side }) {
  const isLeft = side === "left";
  return (
    <div
      aria-hidden="true"
      className="absolute top-0 bottom-0"
      style={{
        [isLeft ? "left" : "right"]: 0,
        width: "50%",
        background:
          "linear-gradient(180deg, #F4EBD9 0%, #EADBB8 40%, #DCC38F 100%)",
        boxShadow: isLeft
          ? "inset -6px 0 12px -6px rgba(60,40,20,0.35), 6px 0 14px -6px rgba(60,40,20,0.35)"
          : "inset 6px 0 12px -6px rgba(60,40,20,0.35), -6px 0 14px -6px rgba(60,40,20,0.35)",
        borderTop: "1px solid rgba(120,80,40,0.35)",
        borderBottom: "1px solid rgba(120,80,40,0.35)",
      }}
    >
      {/* Six-pane divided-light grid — 2 columns × 3 rows */}
      <div
        className="absolute inset-4 grid"
        style={{
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: "1fr 1fr 1fr",
          gap: "10px",
        }}
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="rounded-[2px]"
            style={{
              background:
                "linear-gradient(155deg, rgba(255,247,225,0.65) 0%, rgba(233,210,164,0.55) 100%)",
              border: "1px solid rgba(120,80,40,0.35)",
              boxShadow:
                "inset 0 0 6px rgba(120,80,40,0.15), 0 0 0 3px rgba(244,235,217,0.9)",
            }}
          />
        ))}
      </div>
      {/* Small iron handle — placed on the inner edge of each shutter */}
      <div
        className="absolute rounded-full"
        style={{
          top: "50%",
          [isLeft ? "right" : "left"]: "10px",
          width: "14px",
          height: "44px",
          transform: "translateY(-50%)",
          background:
            "linear-gradient(180deg, #2b2723 0%, #0f0d0b 50%, #2b2723 100%)",
          boxShadow:
            "0 1px 2px rgba(0,0,0,0.35), inset 0 0 2px rgba(255,255,255,0.15)",
        }}
      />
    </div>
  );
}

export default function SaltieShoutOutReveal({ open, onClose, shoutOut }) {
  const reduced = useReducedMotion();

  // Close on ESC.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    // Lock body scroll while the reveal is open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  // Small delay so the shutters are shown CLOSED for a beat before
  // they begin sliding — makes the reveal feel like a real threshold.
  const [phase, setPhase] = useState("closed");
  useEffect(() => {
    if (!open) {
      setPhase("closed");
      return undefined;
    }
    const t1 = setTimeout(() => setPhase("opening"), 350);
    return () => clearTimeout(t1);
  }, [open]);

  const SHUTTER_DUR = reduced ? 0.4 : 2.6;
  const SCALE_DUR = reduced ? 0.4 : 3.0;

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          key="sso-reveal"
          role="dialog"
          aria-modal="true"
          aria-label="Today's Saltie Shout-Out"
          data-testid="sso-reveal"
          className="fixed inset-0 z-[120] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
          onClick={onClose}
          style={{
            // Warm ambient dim — the room softly fades back but is
            // never fully black. Keeps the mood peaceful.
            background:
              "radial-gradient(ellipse at 50% 50%, rgba(60,40,20,0.55) 0%, rgba(30,18,8,0.82) 100%)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
          }}
        >
          {/* Stage — holds the shutters + the letter. Stops backdrop
              clicks that hit the paper from closing the modal. */}
          <div
            className="relative w-full flex items-center justify-center px-4"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "min(46rem, 100%)" }}
          >
            {/* The letter — very slow "camera eases forward" scale. */}
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{
                scale: phase === "opening" ? 1.05 : 1.0,
                opacity: 1,
              }}
              exit={{ scale: 0.98, opacity: 0 }}
              transition={{
                duration: SCALE_DUR,
                ease: [0.4, 0, 0.2, 1],
              }}
              className="relative w-full"
              data-testid="sso-reveal-letter"
            >
              <SaltieShoutOutStationery {...(shoutOut || {})} />
            </motion.div>

            {/* Shutter panels sit ABOVE the letter until they slide
                out of the way. */}
            {!reduced ? (
              <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none overflow-hidden"
                style={{ borderRadius: "6px" }}
              >
                <motion.div
                  className="absolute inset-0"
                  initial={{ x: 0 }}
                  animate={{
                    x: phase === "opening" ? "-105%" : "0%",
                  }}
                  transition={{
                    duration: SHUTTER_DUR,
                    ease: [0.42, 0, 0.28, 1],
                  }}
                >
                  <ShutterPanel side="left" />
                </motion.div>
                <motion.div
                  className="absolute inset-0"
                  initial={{ x: 0 }}
                  animate={{
                    x: phase === "opening" ? "105%" : "0%",
                  }}
                  transition={{
                    duration: SHUTTER_DUR,
                    ease: [0.42, 0, 0.28, 1],
                  }}
                >
                  <ShutterPanel side="right" />
                </motion.div>
              </div>
            ) : null}

            {/* Close pill — quiet, script-styled, top-right of stage. */}
            <button
              type="button"
              onClick={onClose}
              data-testid="sso-reveal-close"
              className="absolute -top-14 right-2 sm:right-4 text-[#F6E3C3] text-sm tracking-[0.24em] uppercase hover:text-white transition-colors"
              style={{
                letterSpacing: "0.24em",
                fontFamily: "'Cormorant Garamond', serif",
              }}
            >
              close the letter ×
            </button>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
